import type {
  ViewConfig,
  ViewFilter,
  ViewSort,
  ViewSystemConfigItem,
  ViewGrouping,
  FilterOperator,
  FilterGroup,
  CreateViewRequest,
  UpdateViewRequest,
  GroupStateConfig,
  GroupLevelStateConfig,
  FooterAggregationConfig,
  ViewColumnConfig,
  ViewGroupSort,
  GroupSortFunction,
} from '@features/views/models'
import { emptyFilterGroup, isFilterGroup } from '@features/views/logic/filters'
import type { Column } from '@features/columns/models'
import { coerceValue } from '@features/filters/logic/filterConstants'

/**
 * Coerce a filter value to match its column's datatype. Filter values can
 * arrive as strings — either from HTML inputs (always strings), from a saved
 * view that the backend stored as strings, or from older clients. This
 * runs at the write boundary so booleans match the column's declared type.
 * Number/currency values intentionally remain decimal strings: coercing them
 * to JavaScript Number here would silently lose integer or fractional digits.
 */
function coerceFilterValueForColumn(
  value: unknown,
  _operator: FilterOperator,
  columnId: string,
  columns: Column[] | undefined
): unknown {
  // No-value operators carry no payload — leave as-is (typically null).
  if (value === null || value === undefined) return value
  if (!columns) return value

  const col = columns.find((c) => c.id === columnId)
  if (!col) return value

  // Only normalize values that are still strings. Already-typed legacy
  // values remain unchanged; newly edited number/currency filters stay as
  // exact decimal strings through serialization.
  if (typeof value !== 'string') return value

  return coerceValue(value, col.type)
}

// ─── Backend → Frontend (response normalization) ─────────────────

interface BackendViewConfig {
  sorts?: BackendSort[] | null
  filters?: BackendFilterNode | null
  group_config?: BackendGroupLevelConfig[] | null
  group_level_states?: BackendGroupLevelState[] | null
  group_states?: BackendGroupState[] | null
  column_ids?: string[] | null
  column_configs?: BackendViewColumnConfig[] | null
  footer_aggregations?: BackendAggregationSpec[] | null
}

/** Matches backend's ViewColumnConfig schema exactly. */
interface BackendViewColumnConfig {
  column_id: string
  is_pinned?: boolean
  is_visible?: boolean | null
}

interface BackendSort {
  column_id: string
  direction: string
  null_placement?: string | null
}

/** A group level's sort: the record sort's shape, with its function. */
interface BackendGroupSort extends BackendSort {
  function?: string | null
}

/** Leaf filter: a single column condition */
interface BackendLeafFilter {
  column_id: string
  val_op_key: string
  val: unknown
  set_oper?: string
  case_sens?: boolean
  negate?: boolean
}

/** Group filter: combines child nodes with AND/OR */
interface BackendGroupFilter {
  bool_op: 'AND' | 'OR'
  items: BackendFilterNode[]
  negate?: boolean
}

/** A filter node is either a leaf or a group */
type BackendFilterNode = BackendLeafFilter | BackendGroupFilter

/** Matches backend's GroupLevelConfig schema exactly. */
interface BackendGroupLevelConfig {
  column_id: string
  sort_by: BackendGroupSort[]
  is_expanded_default?: boolean
  aggregation_overrides?: BackendAggregationSpec[] | null
}

interface BackendAggregationSpec {
  column_id: string
  function: string
}

interface BackendGroupLevelState {
  column_id: string
  is_expanded: boolean
}

interface BackendGroupState {
  group_id: { [column_id: string]: unknown | null }
  is_expanded: boolean
}

interface BackendViewDetailResponse {
  id?: string
  view_id?: string
  view_name?: string
  name?: string
  table_id: string
  is_temp?: boolean
  is_temporary?: boolean
  origin_view_id?: string | null
  session_id?: string | null
  config?: BackendViewConfig
  sorts?: BackendSort[] | null
  filters?: BackendFilterNode | null
  group_config?: BackendGroupLevelConfig[] | null
  column_ids?: string[] | null
  group_level_states?: BackendGroupLevelState[] | null
  group_states?: BackendGroupState[] | null
  column_configs?: BackendViewColumnConfig[] | null
  footer_aggregations?: BackendAggregationSpec[] | null
  system_config?: Array<Record<string, unknown>> | null
}

/** System sorts carry a direction; system filters carry an operator or a group. */
function fromBackendSystemConfigItem(raw: Record<string, unknown>): ViewSystemConfigItem {
  const { display, ...item } = raw
  if ('direction' in item && !('val_op_key' in item) && !('bool_op' in item)) {
    return {
      kind: 'sort',
      sort: fromBackendSort(item as unknown as BackendSort),
      display: display === 'hidden' ? 'hidden' : 'visible',
    }
  }
  return { kind: 'filter', filter: item, display: display === 'visible' ? 'visible' : 'hidden' }
}

/** System sorts the UI shows, read-only, beneath the user's own sorts. */
export function getVisibleSystemSorts(view: ViewConfig | null | undefined): ViewSort[] {
  return (view?.system_config ?? []).flatMap((item) =>
    item.kind === 'sort' && item.display === 'visible' ? [item.sort] : []
  )
}

function fromBackendSort(s: BackendSort): ViewSort {
  return {
    column_id: s.column_id,
    direction: s.direction?.toLowerCase() as 'asc' | 'desc',
  }
}

/**
 * Reverse of the backend's FILTER_OP_MAP: saving a view rewrites frontend
 * operator keys into canonical SQL-ish keys plus a `negate` flag (e.g.
 * `not_equals` → `=` + negate), and reads return that stored form. Without
 * this map the operator select can't find the saved value and renders empty.
 */
const BACKEND_FILTER_OPERATORS: Record<
  string,
  { positive: FilterOperator; negated?: FilterOperator }
> = {
  '=': { positive: 'equals', negated: 'not_equals' },
  CONTAINS: { positive: 'contains', negated: 'not_contains' },
  STARTSWITH: { positive: 'starts_with', negated: 'not_starts_with' },
  ENDSWITH: { positive: 'ends_with', negated: 'not_ends_with' },
  MATCHES: { positive: 'matches_pattern', negated: 'not_matches_pattern' },
  'IS NULL': { positive: 'is_empty', negated: 'is_not_empty' },
  '>': { positive: 'gt' },
  '<': { positive: 'lt' },
  '>=': { positive: 'gte' },
  '<=': { positive: 'lte' },
  'IS ME': { positive: 'is_me' },
}

function fromBackendOperator(
  valOpKey: string | null | undefined,
  negate: boolean | null | undefined
): FilterOperator {
  if (!valOpKey) return 'equals'

  const mapped = BACKEND_FILTER_OPERATORS[valOpKey]
  // Not a canonical key — pass frontend keys (or unknown values) through.
  if (!mapped) return valOpKey as FilterOperator

  return (negate ? mapped.negated : undefined) ?? mapped.positive
}

function fromBackendLeafFilter(f: BackendLeafFilter): ViewFilter {
  return {
    column_id: f.column_id,
    operator: fromBackendOperator(f.val_op_key, f.negate),
    value: f.val,
    ...(f.case_sens ? { case_sensitive: true } : {}),
  }
}

/** Check if a filter node is a group (has bool_op) vs a leaf (has column_id) */
function isGroupFilter(node: BackendFilterNode): node is BackendGroupFilter {
  return 'bool_op' in node && 'items' in node
}

/**
 * Check that a filter node has the minimum shape of a real leaf — a
 * non-empty `column_id`. Used to reject empty objects (`{}`) and partial
 * leaves that the backend sometimes returns in `config.filters`, which
 * would otherwise normalize into a `{ column_id: undefined, ... }` entry that
 * counts as 1 in the badge but renders blank in the modal.
 */
function isLeafFilter(node: BackendFilterNode): node is BackendLeafFilter {
  return 'column_id' in node && typeof (node as BackendLeafFilter).column_id === 'string'
}

/**
 * Recursively parse a backend filter node into a frontend `FilterGroup`
 * tree. Preserves the user's OR/AND conjunctions and nested-group shape
 * — the earlier `flattenFilterNode` discarded both, which is why saved
 * filters with OR or nesting came back wrong on reload.
 *
 * A backend "no filter" or malformed `{}` resolves to an empty root group
 * so consumers can rely on `FilterGroup` being non-null.
 */
export function fromBackendFilterNode(node: BackendFilterNode | null | undefined): FilterGroup {
  if (!node || typeof node !== 'object') return emptyFilterGroup()
  if (isGroupFilter(node)) {
    return {
      conjunction: node.bool_op === 'OR' ? 'OR' : 'AND',
      conditions: node.items
        .map((item) => {
          if (isGroupFilter(item)) return fromBackendFilterNode(item)
          if (isLeafFilter(item)) return fromBackendLeafFilter(item)
          return null
        })
        .filter((c): c is ViewFilter | FilterGroup => c !== null),
    }
  }
  if (isLeafFilter(node)) {
    // A bare leaf at the top level — wrap in a single-condition AND group
    // so callers always work against a `FilterGroup`.
    return { conjunction: 'AND', conditions: [fromBackendLeafFilter(node)] }
  }
  // Empty object / unknown shape — treat as no filter.
  return emptyFilterGroup()
}

function fromBackendGroupSort(s: BackendGroupSort): ViewGroupSort {
  const nullPlacement = s.null_placement?.toLowerCase()
  return {
    column_id: s.column_id,
    direction: s.direction?.toLowerCase() === 'desc' ? 'desc' : 'asc',
    ...(nullPlacement === 'first' || nullPlacement === 'last'
      ? { null_placement: nullPlacement }
      : {}),
    ...(s.function ? { function: s.function as GroupSortFunction } : {}),
  }
}

function fromBackendGrouping(g: BackendGroupLevelConfig): ViewGrouping {
  return {
    column_id: g.column_id,
    sort_by: Array.isArray(g.sort_by) ? g.sort_by.map(fromBackendGroupSort) : [],
    default_state: g.is_expanded_default === false ? 'collapsed' : 'expanded',
    aggregation_overrides: Array.isArray(g.aggregation_overrides)
      ? g.aggregation_overrides.map(fromBackendFooterAggregation)
      : [],
  }
}

function fromBackendGroupState(s: BackendGroupState): GroupStateConfig {
  return { group_id: s.group_id, is_expanded: s.is_expanded }
}

function fromBackendGroupLevelState(s: BackendGroupLevelState): GroupLevelStateConfig {
  return { column_id: s.column_id, is_expanded: s.is_expanded }
}

function fromBackendFooterAggregation(s: BackendAggregationSpec): FooterAggregationConfig {
  return {
    column_id: s.column_id,
    function: s.function as FooterAggregationConfig['function'],
  }
}

function fromBackendColumnConfig(c: BackendViewColumnConfig): ViewColumnConfig {
  return {
    column_id: c.column_id,
    is_pinned: c.is_pinned ?? false,
    ...(typeof c.is_visible === 'boolean' ? { is_visible: c.is_visible } : {}),
  }
}

/**
 * Normalizes a backend view response into the frontend ViewConfig shape.
 * Handles both nested (config: {...}) and flat response shapes.
 * Maps backend naming (val_op_key, group_config) to frontend (operator, grouping).
 */
export function normalizeViewConfig(raw: unknown): ViewConfig {
  if (raw == null || typeof raw !== 'object') {
    throw new Error('View API returned an empty response.')
  }

  const r = raw as BackendViewDetailResponse

  // Pydantic uses field names for serialization: `id` not `view_id`
  const viewId = r.id ?? r.view_id ?? ''
  const viewName = r.view_name ?? r.name ?? ''
  const warnings: string[] = []

  // Extract config — may be nested under `config` or flat on the object
  const cfg = r.config ?? {}
  const rawSorts = cfg.sorts ?? r.sorts
  const rawFilters = cfg.filters ?? r.filters
  const rawGroups = cfg.group_config ?? r.group_config
  const rawColumnIds = cfg.column_ids ?? r.column_ids
  const rawGroupStates = cfg.group_states ?? r.group_states
  const rawGroupLevelStates = cfg.group_level_states ?? r.group_level_states
  const rawFooterAggs = cfg.footer_aggregations ?? r.footer_aggregations
  const rawColumnConfigs = cfg.column_configs ?? r.column_configs

  // Map sorts
  let sorts: ViewSort[] = []
  if (Array.isArray(rawSorts)) {
    sorts = rawSorts.map(fromBackendSort)
  } else if (rawSorts != null) {
    warnings.push(`view ${viewId}: sorts was ${typeof rawSorts}, normalized to []`)
  }

  // Map filters — preserve the backend's recursive bool_op/items tree so
  // OR conjunctions and nested groups survive the round trip. The earlier
  // flat-array shape silently lost both.
  const filters: FilterGroup = fromBackendFilterNode(rawFilters as BackendFilterNode | null)

  // Map grouping
  let grouping: ViewGrouping[] = []
  if (Array.isArray(rawGroups)) {
    grouping = rawGroups.map(fromBackendGrouping)
  } else if (rawGroups != null) {
    warnings.push(`view ${viewId}: grouping was ${typeof rawGroups}, normalized to []`)
  }

  // Column IDs
  const columnIds = Array.isArray(rawColumnIds) ? rawColumnIds : []
  if (!Array.isArray(rawColumnIds) && rawColumnIds != null) {
    warnings.push(`view ${viewId}: column_ids was ${typeof rawColumnIds}, normalized to []`)
  }

  // Group states / level states / footer aggregations / column configs (new)
  const groupStates = Array.isArray(rawGroupStates) ? rawGroupStates.map(fromBackendGroupState) : []
  const groupLevelStates = Array.isArray(rawGroupLevelStates)
    ? rawGroupLevelStates.map(fromBackendGroupLevelState)
    : []
  const footerAggregations = Array.isArray(rawFooterAggs)
    ? rawFooterAggs.map(fromBackendFooterAggregation)
    : []
  const columnConfigs = Array.isArray(rawColumnConfigs)
    ? rawColumnConfigs.map(fromBackendColumnConfig)
    : []

  if (warnings.length > 0) {
    console.warn('[API Normalizer] ViewConfig issues:', warnings)
  }

  return {
    id: viewId,
    name: viewName,
    table_id: r.table_id,
    owner_id: '',
    is_default: false,
    is_temporary: r.is_temporary ?? r.is_temp ?? false,
    origin_view_id: r.origin_view_id ?? null,
    session_id: r.session_id ?? null,
    column_ids: columnIds,
    column_configs: columnConfigs,
    filters,
    sorts,
    grouping,
    aggregations: {},
    group_states: groupStates,
    group_level_states: groupLevelStates,
    footer_aggregations: footerAggregations,
    system_config: Array.isArray(r.system_config)
      ? r.system_config.map(fromBackendSystemConfigItem)
      : [],
    created_at: '',
    updated_at: '',
  }
}

// ─── Frontend → Backend (request serialization) ──────────────────

function toBackendSort(s: ViewSort): BackendSort {
  return {
    column_id: s.column_id,
    direction: s.direction.toUpperCase() as BackendSort['direction'],
  }
}

function toBackendLeafFilter(f: ViewFilter, columns?: Column[]): BackendLeafFilter {
  return {
    column_id: f.column_id,
    val_op_key: f.operator,
    val: coerceFilterValueForColumn(f.value, f.operator, f.column_id, columns),
    ...(f.case_sensitive ? { case_sens: true } : {}),
  }
}

/**
 * Convert a frontend `FilterGroup` tree into a backend `BackendFilterNode`
 * (dict), or `null` when the tree holds no conditions.
 *
 * An explicit `null` clears the stored filters: the backend's view update
 * distinguishes a `filters` key that was sent (even as null) from one that
 * was omitted, and only the omitted case keeps the stored filters.
 *
 * Top-level shortcut: a root group with exactly one leaf and no nested
 * groups serializes as the bare leaf, matching the backend's
 * single-condition shorthand.
 *
 * Conjunctions and nested structure are preserved verbatim — collapsing
 * same-conjunction groups would lose the shape Michael's UX wants to
 * keep visible.
 */
export function toBackendFilterNode(
  group: FilterGroup,
  columns?: Column[]
): BackendFilterNode | null {
  if (group.conditions.length === 0) {
    return null
  }

  // Single bare leaf at root → emit the leaf directly.
  if (group.conditions.length === 1 && !isFilterGroup(group.conditions[0])) {
    return toBackendLeafFilter(group.conditions[0] as ViewFilter, columns)
  }

  const items = group.conditions
    .map((c) =>
      isFilterGroup(c) ? toBackendFilterNode(c, columns) : toBackendLeafFilter(c, columns)
    )
    .filter((item): item is BackendFilterNode => item !== null)

  if (items.length === 0) {
    return null
  }

  if (items.length === 1) {
    return items[0]
  }

  return {
    bool_op: group.conjunction,
    items,
  }
}

function toBackendGroupSort(s: ViewGroupSort): BackendGroupSort {
  return {
    column_id: s.column_id,
    direction: s.direction.toUpperCase(),
    ...(s.null_placement ? { null_placement: s.null_placement.toUpperCase() } : {}),
    ...(s.function ? { function: s.function } : {}),
  }
}

function toBackendGrouping(g: ViewGrouping): BackendGroupLevelConfig {
  return {
    column_id: g.column_id,
    sort_by: g.sort_by.map(toBackendGroupSort),
    is_expanded_default: g.default_state === 'expanded',
    aggregation_overrides: g.aggregation_overrides?.length
      ? g.aggregation_overrides.map(toBackendFooterAggregation)
      : null,
  }
}

function toBackendGroupState(s: GroupStateConfig): BackendGroupState {
  return { group_id: s.group_id, is_expanded: s.is_expanded }
}

/** The backend rejects a state whose group_id names no grouped column. */
function toBackendGroupStates(states: GroupStateConfig[]): BackendGroupState[] {
  return states.filter((state) => Object.keys(state.group_id).length > 0).map(toBackendGroupState)
}

function toBackendGroupLevelState(s: GroupLevelStateConfig): BackendGroupLevelState {
  return { column_id: s.column_id, is_expanded: s.is_expanded }
}

function toBackendFooterAggregation(s: FooterAggregationConfig): BackendAggregationSpec {
  return { column_id: s.column_id, function: s.function }
}

function toBackendColumnConfig(c: ViewColumnConfig): BackendViewColumnConfig {
  return {
    column_id: c.column_id,
    is_pinned: c.is_pinned,
    ...(c.is_visible === undefined ? {} : { is_visible: c.is_visible }),
  }
}

/** Serialize a frontend CreateViewRequest into the backend's expected shape. */
export function toBackendCreateView(
  req: CreateViewRequest,
  options?: { is_temp?: boolean; origin_view_id?: string | null; session_id?: string | null },
  columns?: Column[]
) {
  return {
    view_name: req.name,
    table_id: req.table_id,
    is_temp: options?.is_temp ?? false,
    origin_view_id: options?.origin_view_id ?? null,
    session_id: options?.session_id ?? null,
    config: toBackendViewConfig(req, columns),
  }
}

/**
 * Serialize a complete view config into the backend's ViewConfig shape, with
 * empty lists sent as null. Used to create views and to send the config on
 * screen with a records read (see queryRecords).
 */
export function toBackendViewConfig(
  req: Omit<CreateViewRequest, 'table_id' | 'name' | 'description'>,
  columns?: Column[]
) {
  return {
    sorts: req.sorts?.length ? req.sorts.map(toBackendSort) : null,
    filters: toBackendFilterNode(req.filters ?? emptyFilterGroup(), columns),
    group_config: req.grouping?.length ? req.grouping.map(toBackendGrouping) : null,
    group_states: req.group_states?.length ? toBackendGroupStates(req.group_states) : null,
    group_level_states: req.group_level_states?.length
      ? req.group_level_states.map(toBackendGroupLevelState)
      : null,
    footer_aggregations: req.footer_aggregations?.length
      ? req.footer_aggregations.map(toBackendFooterAggregation)
      : null,
    column_ids: req.column_ids?.length ? req.column_ids : null,
    column_configs: req.column_configs?.length
      ? req.column_configs.map(toBackendColumnConfig)
      : null,
  }
}

/**
 * Serialize a frontend UpdateViewRequest into the backend's expected shape.
 *
 * The backend treats an omitted list field as "keep the stored value" and an
 * empty list as "clear it", so a field the caller passed must be sent even
 * when it is empty — otherwise removing the last sort or group never reaches
 * the server.
 */
export function toBackendUpdateView(req: UpdateViewRequest, columns?: Column[]) {
  return {
    view_name: req.name ?? undefined,
    is_temp: req.is_temporary ?? undefined,
    config: {
      sorts: req.sorts ? req.sorts.map(toBackendSort) : undefined,
      filters: req.filters ? toBackendFilterNode(req.filters, columns) : undefined,
      group_config: req.grouping ? req.grouping.map(toBackendGrouping) : undefined,
      group_states: req.group_states ? toBackendGroupStates(req.group_states) : undefined,
      group_level_states: req.group_level_states
        ? req.group_level_states.map(toBackendGroupLevelState)
        : undefined,
      footer_aggregations: req.footer_aggregations
        ? req.footer_aggregations.map(toBackendFooterAggregation)
        : undefined,
      column_ids: req.column_ids ?? undefined,
      column_configs: req.column_configs
        ? req.column_configs.map(toBackendColumnConfig)
        : undefined,
    },
  }
}
